import React from "react";
import {
  Avatar,
  Box,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import { Organisation, User, UserType } from "../../../types";
import TextCopyChip from "../../../components/chips/text-copy-chip";
import OrganisationMembershipItem from "./team-membership-item";
import EditableRoleChip from "../../../components/chips/editable-role-chip";

interface UsersTableRowProps {
  user: User;
  teams: Organisation[];
}

const UsersTableRow: React.FC<UsersTableRowProps> = ({
  user,
  teams,
}) => {
  const isGuest = user.userType === UserType.Guest;
  const displayName = user.fullName || "Unknown User";
  const avatarName = user.fullName || user.email || "Guest";
  const initials =
    avatarName
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || "G";

  return (
    <TableRow
      sx={{
        "&:hover": {
          background:
            "linear-gradient(135deg, rgba(5, 84, 128, 0.08) 0%, rgba(0, 150, 136, 0.08) 100%)",
        },
      }}
    >
      <TableCell>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {isGuest ? (
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                backgroundColor: "#055480",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <PersonIcon sx={{ fontSize: 18 }} />
            </Box>
          ) : (
            <Avatar
              sx={{
                backgroundColor: "#055480",
                color: "primary.contrastText",
                width: 30,
                height: 30,
                fontSize: "0.875rem",
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              <Typography variant="body2">{initials}</Typography>
            </Avatar>
          )}

          <Stack spacing={0.15} sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                lineHeight: 1.1,
                fontSize: { xs: "0.92rem", sm: "1rem" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayName}
            </Typography>

            {user.jobTitle && !isGuest && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.1, fontSize: "0.8rem" }}
              >
                {user.jobTitle}
              </Typography>
            )}
          </Stack>
        </Stack>
      </TableCell>

      <TableCell>
        {user.email ? (
          <TextCopyChip text={user.email} />
        ) : (
          <Typography variant="caption" color="text.secondary">
            No email
          </Typography>
        )}
      </TableCell>

      <TableCell>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {teams.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 0.5, textAlign: "center" }}
            >
              Not part of any teams
            </Typography>
          ) : (
            teams.map((team) => {
              return (
                <OrganisationMembershipItem
                  key={`${team.organisationId}-${user.userId}-list`}
                  user={user}
                  team={team}
                />
              );
            })
          )}
        </Stack>
      </TableCell>

      <TableCell align="right">
        <EditableRoleChip user={user} />
      </TableCell>
    </TableRow>
  );
};

export default UsersTableRow;
